import { all, call, fork, put, takeLatest } from "redux-saga/effects";
import { publicResourceProvider } from "../../constants/actionTypes";
import { statusCode } from "../../constants/apis/apiStatus";
import { toasterFlags } from "../../constants/componentConstants";
import { toastNotification } from "../../utils/toast";
import {
    cloudProviderAzureError, cloudProviderAzureSuccess,
    cloudVMLoading, cloudVMOperationError, cloudVMOperationSuccess,
    getCloudVMListError, getCloudVMListSuccess,
    fetchInstanceSizeSuccess, fetchInstanceSizeError,
    createAzureVmTemplateSuccess, createAzureVmTemplateError,
    updateAzureVmTemplateSuccess, updateAzureVmTemplateError,
    fetchAzureVmTemplateSuccess, fetchAzureVmTemplateError,
    listAzureVmTemplatesSuccess, listAzureVmTemplatesError,
    deleteAzureVmTemplateSuccess, deleteAzureVmTemplateError,
    publishAzureVmTemplateSuccess, publishAzureVmTemplateError,
    listAzureSkusSuccess, listAzureSkusError,
    createAzureSkuSuccess, createAzureSkuError,
    updateAzureSkuSuccess, updateAzureSkuError,
} from "../actions";
import {
    cloudProviderAzureListApi,
    cloudVMOperationApi,
    fetchCloudVMList,
    fetchAzureResourceApi,
    fetchAzureInstanceSizesApi,
    createAzureVmTemplateApi,
    updateAzureVmTemplateApi,
    fetchAzureVmTemplateApi,
    listAzureVmTemplatesApi,
    deleteAzureVmTemplateApi,
    publishAzureVmTemplateApi,
    listAzureSkusApi,
    createAzureSkuApi,
    updateAzureSkuApi,
} from "../api/publicResourceManagerApi";

export function* fetchCloudVMListSaga({ payload }) {
    try {
        yield put(cloudVMLoading(true));
        const data = yield call(fetchCloudVMList, payload);

        if (data?.status === statusCode.Ok200) {
            yield put(getCloudVMListSuccess(data));
        } else {
            yield put(getCloudVMListError(data));
        }
    } catch (err) {
        yield put(getCloudVMListError(err));
    } finally {
        yield put(cloudVMLoading(false));
    }
}

export function* cloudVMOperationSaga({ payload }) {
    try {
        yield put(cloudVMLoading(true));
        const data = yield call(cloudVMOperationApi, payload);

        if (data?.status === statusCode.Ok200) {
            // toastNotification(toasterFlags.INFO, data?.data?.message);
            yield put(cloudVMOperationSuccess(data));
            toastNotification(toasterFlags.SUCCESS, data?.data?.message);
        } else {
            toastNotification(toasterFlags.WARN, data?.data?.message);
            yield put(cloudVMOperationError(data));
        }
    } catch (err) {
        toastNotification(toasterFlags.WARN, err?.message);
        yield put(cloudVMOperationError(err));
    } finally {
        yield put(cloudVMLoading(false));
    }
}

export function* cloudProviderAzureListSaga({ payload }) {
    try {
        yield put(cloudVMLoading(true));
        const data = yield call(cloudProviderAzureListApi, payload);

        if (data?.status === statusCode.Ok200) {
            yield put(cloudProviderAzureSuccess(data));
        } else {
            yield put(cloudProviderAzureError(data));
        }
    } catch (err) {
        yield put(cloudProviderAzureError(err));
    } finally {
        yield put(cloudVMLoading(false));
    }
}

export function* fetchAzureCascadeDropdownSaga({ payload }) {
    const { url, params, onSuccess, onError } = payload;
    try {
        const data = yield call(fetchAzureResourceApi, url, params);
        if (data?.status === statusCode.Ok200) {
            yield put(onSuccess(Array.isArray(data?.data) ? data.data : []));
        } else {
            yield put(onError(data));
        }
    } catch (err) {
        yield put(onError(err));
    }
}

export function* fetchInstanceSizeSaga({ payload }) {
    try {
        const data = yield call(fetchAzureInstanceSizesApi, payload);
        if (data?.status === statusCode.Ok200) {
            yield put(fetchInstanceSizeSuccess(Array.isArray(data?.data) ? data.data : []));
        } else {
            yield put(fetchInstanceSizeError(data));
        }
    } catch (err) {
        yield put(fetchInstanceSizeError(err));
    }
}

export function* createAzureVmTemplateSaga({ payload }) {
    const { provider_id } = payload;          // used in URL query param
    try {
        const data = yield call(createAzureVmTemplateApi, provider_id, payload); // full payload (incl. provider_id) as body
        if (data?.status === statusCode.Ok200 || data?.status === statusCode.Created201) {
            toastNotification(toasterFlags.SUCCESS, data?.data?.message || "Template created successfully");
            yield put(createAzureVmTemplateSuccess(data?.data));
        } else {
            toastNotification(toasterFlags.ERROR, data?.data?.message || "Failed to create template");
            yield put(createAzureVmTemplateError(data));
        }
    } catch (err) {
        toastNotification(toasterFlags.ERROR, err?.message);
        yield put(createAzureVmTemplateError(err));
    }
}

export function* updateAzureVmTemplateSaga({ payload }) {
    const { data: formData } = payload;
    const { provider_id, template_name } = formData; // used in URL query params
    try {
        const data = yield call(updateAzureVmTemplateApi, provider_id, template_name, formData);
        if (data?.status === statusCode.Ok200) {
            toastNotification(toasterFlags.SUCCESS, data?.data?.message || "Template updated successfully");
            yield put(updateAzureVmTemplateSuccess(data?.data));
        } else {
            toastNotification(toasterFlags.ERROR, data?.data?.message || "Failed to update template");
            yield put(updateAzureVmTemplateError(data));
        }
    } catch (err) {
        toastNotification(toasterFlags.ERROR, err?.message);
        yield put(updateAzureVmTemplateError(err));
    }
}

export function* fetchAzureVmTemplateSaga({ payload }) {
    const { provider_id, template_id } = payload;
    try {
        const data = yield call(fetchAzureVmTemplateApi, provider_id, template_id);
        if (data?.status === statusCode.Ok200) {
            yield put(fetchAzureVmTemplateSuccess(data?.data));
        } else {
            yield put(fetchAzureVmTemplateError(data));
        }
    } catch (err) {
        yield put(fetchAzureVmTemplateError(err));
    }
}

export function* listAzureVmTemplatesSaga({ payload: params }) {
    try {
        const data = yield call(listAzureVmTemplatesApi, params);
        if (data?.status === statusCode.Ok200) {
            yield put(listAzureVmTemplatesSuccess(data?.data));
        } else {
            yield put(listAzureVmTemplatesError(data));
        }
    } catch (err) {
        yield put(listAzureVmTemplatesError(err));
    }
}

export function* deleteAzureVmTemplateSaga({ payload }) {
    const { provider_id, template_name } = payload;
    try {
        const data = yield call(deleteAzureVmTemplateApi, provider_id, template_name);
        if (data?.status === statusCode.Ok200 || data?.status === statusCode.NoContent204) {
            toastNotification(toasterFlags.SUCCESS, data?.data?.message || "Template deleted successfully");
            yield put(deleteAzureVmTemplateSuccess(data?.data));
        } else {
            toastNotification(toasterFlags.ERROR, data?.data?.message || "Failed to delete template");
            yield put(deleteAzureVmTemplateError(data));
        }
    } catch (err) {
        toastNotification(toasterFlags.ERROR, err?.message);
        yield put(deleteAzureVmTemplateError(err));
    }
}

export function* publishAzureVmTemplateSaga({ payload }) {
    const { provider_id, template_name, ...cleanPublishPayload } = payload;
    try {
        // First Call: Actual Publish
        const publishResponse = yield call(publishAzureVmTemplateApi, cleanPublishPayload);

        if (publishResponse?.status === statusCode.Ok200 || publishResponse?.status === statusCode.Created201) {
            // Second Call: Update is_published status
            const updateResponse = yield call(updateAzureVmTemplateApi, provider_id, template_name, { is_published: true });

            if (updateResponse?.status === statusCode.Ok200) {
                toastNotification(toasterFlags.SUCCESS, publishResponse?.data?.message || "Template published successfully");
                yield put(publishAzureVmTemplateSuccess(publishResponse?.data));
            } else {
                toastNotification(toasterFlags.ERROR, updateResponse?.data?.message || "Failed to update template status after publication");
                yield put(publishAzureVmTemplateError(updateResponse));
            }
        } else {
            toastNotification(toasterFlags.ERROR, publishResponse?.data?.message || "Failed to publish template");
            yield put(publishAzureVmTemplateError(publishResponse));
        }
    } catch (err) {
        toastNotification(toasterFlags.ERROR, err?.message);
        yield put(publishAzureVmTemplateError(err));
    }
}

export function* listAzureSkusSaga({ payload: params }) {
    try {
        const data = yield call(listAzureSkusApi, params);
        if (data?.status === statusCode.Ok200) {
            yield put(listAzureSkusSuccess(data?.data));
        } else {
            yield put(listAzureSkusError(data));
        }
    } catch (err) {
        yield put(listAzureSkusError(err));
    }
}

export function* createAzureSkuSaga({ payload }) {
    try {
        const data = yield call(createAzureSkuApi, payload);
        if (data?.status === statusCode.Ok200 || data?.status === statusCode.Created201) {
            toastNotification(toasterFlags.SUCCESS, data?.data?.message || "SKU created successfully");
            yield put(createAzureSkuSuccess(data?.data));
        } else {
            const errorMsg = data?.data?.message || data?.message || "Failed to create SKU";
            toastNotification(toasterFlags.ERROR, errorMsg);
            yield put(createAzureSkuError(data));
        }
    } catch (err) {
        toastNotification(toasterFlags.ERROR, err?.message || "An error occurred");
        yield put(createAzureSkuError(err));
    }
}

export function* updateAzureSkuSaga({ payload }) {
    const { skuId, data: skuData } = payload;
    try {
        const data = yield call(updateAzureSkuApi, skuId, skuData);
        if (data?.status === statusCode.Ok200) {
            toastNotification(toasterFlags.SUCCESS, data?.data?.message || "SKU updated successfully");
            yield put(updateAzureSkuSuccess(data?.data));
        } else {
            const errorMsg = data?.data?.message || data?.message || "Failed to update SKU";
            toastNotification(toasterFlags.ERROR, errorMsg);
            yield put(updateAzureSkuError(data));
        }
    } catch (err) {
        toastNotification(toasterFlags.ERROR, err?.message || "An error occurred");
        yield put(updateAzureSkuError(err));
    }
}

export function* publicResourceManagerRootSaga() {
    yield takeLatest(publicResourceProvider.GET_CLOUD_VM_LIST_ACTION, fetchCloudVMListSaga);
    yield takeLatest(publicResourceProvider.CLOUD_VM_OPERATION_ACTION, cloudVMOperationSaga);
    yield takeLatest(publicResourceProvider.CLOUD_PROVIDER_AZURE_LIST_ACTION, cloudProviderAzureListSaga);
    yield takeLatest(publicResourceProvider.FETCH_RESOURCE_GROUPS_ACTION, fetchAzureCascadeDropdownSaga);
    yield takeLatest(publicResourceProvider.FETCH_COMPUTE_GALLERY_ACTION, fetchAzureCascadeDropdownSaga);
    yield takeLatest(publicResourceProvider.FETCH_GALLERY_IMAGE_ACTION, fetchAzureCascadeDropdownSaga);
    yield takeLatest(publicResourceProvider.FETCH_INSTANCE_SIZE_ACTION, fetchInstanceSizeSaga);
    yield takeLatest(publicResourceProvider.CREATE_AZURE_VM_TEMPLATE_ACTION, createAzureVmTemplateSaga);
    yield takeLatest(publicResourceProvider.UPDATE_AZURE_VM_TEMPLATE_ACTION, updateAzureVmTemplateSaga);
    yield takeLatest(publicResourceProvider.FETCH_AZURE_VM_TEMPLATE_ACTION, fetchAzureVmTemplateSaga);
    yield takeLatest(publicResourceProvider.LIST_AZURE_VM_TEMPLATES_ACTION, listAzureVmTemplatesSaga);
    yield takeLatest(publicResourceProvider.DELETE_AZURE_VM_TEMPLATE_ACTION, deleteAzureVmTemplateSaga);
    yield takeLatest(publicResourceProvider.PUBLISH_AZURE_VM_TEMPLATE_ACTION, publishAzureVmTemplateSaga);
    yield takeLatest(publicResourceProvider.LIST_AZURE_SKUS_ACTION, listAzureSkusSaga);
    yield takeLatest(publicResourceProvider.CREATE_AZURE_SKU_ACTION, createAzureSkuSaga);
    yield takeLatest(publicResourceProvider.UPDATE_AZURE_SKU_ACTION, updateAzureSkuSaga);
}

export default function* rootSaga() {
    yield all([fork(publicResourceManagerRootSaga)])
}
